/** XeAgents.ai **/
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Phone, Plus, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { HealthcareSettings } from "./ClinicPanel";

interface Receptionist {
  id: string;
  name: string;
  language: string | null;
  phoneNumber: string | null;
  extraInstructions: string | null;
  knowledgeBaseIds: string[] | null;
}

interface KnowledgeItem {
  id: string;
  title: string;
}

export default function ReceptionistPanel({ settings }: { settings: HealthcareSettings | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Receptionist | "new" | null>(null);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [rules, setRules] = useState("");
  const [kbIds, setKbIds] = useState<string[]>([]);

  const { data: agents = [], isLoading } = useQuery<Receptionist[]>({ queryKey: ["/api/healthcare/agents"], enabled: !!settings });
  const open = editing !== null;

  const { data: languageInfo } = useQuery<{ defaultLanguage: string; languages: Array<{ code: string; name: string }> }>({
    queryKey: ["/api/agents/nova-sonic/languages"],
    enabled: open,
  });
  const { data: numbers = [] } = useQuery<any[]>({
    queryKey: ["/api/phone-numbers", "healthcare"],
    queryFn: async () => (await apiRequest("GET", "/api/phone-numbers?excludeAssigned=true")).json(),
    enabled: editing === "new",
    staleTime: 0,
  });
  const plivoNumbers = useMemo(() => numbers.filter((n) => n._source === "plivo"), [numbers]);
  const { data: kbRaw } = useQuery<any>({ queryKey: ["/api/rag-knowledge"], enabled: open });
  const knowledge: KnowledgeItem[] = useMemo(() => (Array.isArray(kbRaw) ? kbRaw : kbRaw?.items ?? []).map((k: any) => ({ id: k.id, title: k.title || k.name || k.id })), [kbRaw]);

  useEffect(() => {
    if (!open) return;
    if (editing === "new") {
      setName("");
      setPhoneNumberId("");
      setRules("");
      setKbIds([]);
      setLanguage("");
    } else if (editing) {
      setName(editing.name);
      setRules(editing.extraInstructions ?? "");
      setKbIds(editing.knowledgeBaseIds ?? []);
    }
  }, [editing, open]);

  useEffect(() => {
    if (editing === "new" && !language && languageInfo) setLanguage(languageInfo.defaultLanguage);
  }, [editing, language, languageInfo]);

  const save = useMutation({
    mutationFn: async () => {
      const res =
        editing === "new"
          ? await apiRequest("POST", "/api/healthcare/agents", { name: name.trim(), language, phoneNumberId, extraInstructions: rules.trim(), knowledgeBaseIds: kbIds })
          : await apiRequest("PATCH", `/api/healthcare/agents/${(editing as Receptionist).id}`, { extraInstructions: rules.trim(), knowledgeBaseIds: kbIds });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editing === "new" ? "Receptionist created" : "Receptionist updated" });
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      setEditing(null);
    },
    onError: (e: any) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  if (!settings) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">Save your clinic details first. The receptionist needs your clinic name, time zone and opening hours.</CardContent>
      </Card>
    );
  }

  const canSave = editing === "new" ? name.trim().length > 0 && !!phoneNumberId && !!language : true;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Inbound receptionist</h3>
          <p className="text-sm text-muted-foreground">Answers calls to one of your numbers: books, moves and cancels appointments, answers routine questions, and hands over to your front desk.</p>
        </div>
        <Button onClick={() => setEditing("new")} data-testid="button-add-receptionist">
          <Plus className="mr-2 h-4 w-4" /> New receptionist
        </Button>
      </div>

      {!isLoading && agents.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
            <Stethoscope className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No receptionist yet. Create one and point it at your clinic's phone number.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {agents.map((a) => (
          <Card key={a.id} data-testid={`receptionist-${a.id}`}>
            <CardContent className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{a.name}</p>
                  <p className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" /> {a.phoneNumber ?? "No number"}
                  </p>
                </div>
                <Button size="icon" variant="ghost" aria-label={`Edit ${a.name}`} onClick={() => setEditing(a)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {a.knowledgeBaseIds?.length ? `${a.knowledgeBaseIds.length} clinic document${a.knowledgeBaseIds.length > 1 ? "s" : ""} for questions` : "No clinic documents: it will not answer questions about fees or hours"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New receptionist" : "Edit receptionist"}</DialogTitle>
            <DialogDescription>Clinic name, hours, doctors and your integration come from the other tabs, so a change there applies to the next call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editing === "new" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="rec-name">Name callers hear</Label>
                  <Input id="rec-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="Mia" data-testid="input-receptionist-name" />
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="select-receptionist-language">
                      <SelectValue placeholder="Choose a language" />
                    </SelectTrigger>
                    <SelectContent>
                      {(languageInfo?.languages ?? []).map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Phone number it answers</Label>
                  <Select value={phoneNumberId} onValueChange={setPhoneNumberId}>
                    <SelectTrigger data-testid="select-receptionist-number">
                      <SelectValue placeholder={plivoNumbers.length ? "Choose a number" : "No free Plivo number"} />
                    </SelectTrigger>
                    <SelectContent>
                      {plivoNumbers.map((n) => (
                        <SelectItem key={n.id} value={String(n.id)}>
                          {n.friendlyName || n.phoneNumber}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Fixed once created. Callers on this number reach the receptionist.</p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Clinic documents for questions</Label>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border p-3">
                {knowledge.length === 0 && <p className="text-sm text-muted-foreground">No documents yet. Add opening hours, fees, parking and visit preparation in Knowledge Base, then choose them here.</p>}
                {knowledge.map((k) => (
                  <label key={k.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={kbIds.includes(k.id)} onCheckedChange={(c) => setKbIds((ids) => (c ? [...ids, k.id] : ids.filter((i) => i !== k.id)))} />
                    {k.title}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">The receptionist answers only from these. Anything else becomes a message to your team.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rec-rules">Extra rules (optional)</Label>
              <Textarea id="rec-rules" rows={4} value={rules} onChange={(e) => setRules(e.target.value)} placeholder="For example: new patients need a 30 minute first visit. Do not book children under 2 with Dr Lee." />
              <p className="text-xs text-muted-foreground">The safety rules (no medical advice, identity check, emergencies) always apply and cannot be turned off here.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !canSave} data-testid="button-save-receptionist">
              {save.isPending ? "Saving…" : editing === "new" ? "Create receptionist" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
